/**
 * Fold Persian (۰-۹) and Arabic-Indic (٠-٩) digits to ASCII.
 *
 * Every Persian keyboard layout — and every in-app keyboard in Bale and Eitaa
 * — produces «۵», not "5". Nothing downstream understands that: `Number()`
 * gives NaN, yup's `.number()` fails, `<input type="number">` throws the
 * character away before React ever sees it, and the backend rejects the
 * payload. So «۵۰۰۰۰» typed the way people actually type it has to become
 * "50000" at the point it enters the app.
 *
 * Anything that takes a number from a human goes through here.
 * `installPersianDigitInput` (utilities/persianDigits) applies it to every
 * input in the app; the explicit calls that remain are the belt to that
 * braces, for values that arrive from somewhere other than a keystroke.
 *
 * Only the digits are folded. The Persian decimal and thousands separators
 * («٫» and «٬») are deliberately left alone: they appear in *formatted*
 * values — `faFull.format()` output rendered back into an input, as MoneyInput
 * does — and rewriting them mid-typing would fight the formatter over the
 * caret.
 */
const PERSIAN_ZERO = 0x06f0
const ARABIC_ZERO = 0x0660

/** Matches a digit that only a Persian reader can parse. */
const NON_LATIN_DIGIT = /[۰-۹٠-٩]/
const NON_LATIN_DIGITS = /[۰-۹٠-٩]/g

export function toEnglishDigits(str: string): string {
    return String(str ?? '').replace(NON_LATIN_DIGITS, (char) => {
        const code = char.charCodeAt(0)
        return String(
            code - (code >= PERSIAN_ZERO ? PERSIAN_ZERO : ARABIC_ZERO)
        )
    })
}

/** The same fold, under the name the cashbox screens already use. */
export const toLatinDigits = toEnglishDigits

/** True when folding `str` would change it — cheap enough to test first. */
export function hasNonLatinDigits(str: string): boolean {
    return NON_LATIN_DIGIT.test(String(str ?? ''))
}
